import { createContext, useContext, useState, useEffect } from 'react';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState(() => {
    const stored = localStorage.getItem('notifications_history');
    return stored ? JSON.parse(stored) : [];
  });

  const [latestAlert, setLatestAlert] = useState(null);

  const sendNotification = async (type, message, crypto, price, alertData = {}) => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const recentNotification = notifications.find(n =>
      n.crypto === crypto &&
      n.type === type &&
      new Date(n.timestamp) > oneDayAgo
    );

    if (recentNotification) {
      console.log(`Notification for ${crypto} already sent within the last 24 hours. Skipping.`);
      return;
    }

    const notification = {
      id: Date.now(),
      type,
      message,
      crypto,
      price,
      timestamp: new Date().toISOString(),
      read: false
    };

    setNotifications((prev) => {
      const updated = [notification, ...prev].slice(0, 100);
      localStorage.setItem('notifications_history', JSON.stringify(updated));
      return updated;
    });

    // Trigger popup for alerts
    if (type === 'alert') {
      setLatestAlert(notification);
    }

    if (type === 'alert' && alertData.email) {
      try {
        const { sendAlertEmail } = await import('../api/client');
        const result = await sendAlertEmail({
          email: alertData.email,
          crypto: crypto,
          symbol: alertData.symbol || crypto,
          condition: alertData.condition || 'above',
          target_price: alertData.targetPrice || price,
          current_price: price
        });
        console.log(`✓ Email notification sent to ${alertData.email} for ${crypto} at $${price}`, result);
      } catch (error) {
        console.error('✗ Failed to send email notification:', error);
        console.error('Error details:', {
          email: alertData.email,
          crypto: crypto,
          symbol: alertData.symbol,
          error: error.message,
          response: error.response?.data
        });
      }
    } else if (type === 'alert' && !alertData.email) {
      console.warn('⚠ Cannot send email: user email is missing', { user: alertData });
    }
  };

  const markAsRead = (id) => {
    setNotifications((prev) => {
      const updated = prev.map((n) => (n.id === id ? { ...n, read: true } : n));
      localStorage.setItem('notifications_history', JSON.stringify(updated));
      return updated;
    });
  };

  const clearAll = () => {
    setNotifications([]);
    localStorage.removeItem('notifications_history');
  };

  const value = {
    notifications,
    latestAlert,
    setLatestAlert,
    sendNotification,
    markAsRead,
    clearAll,
    unreadCount: notifications.filter((n) => !n.read).length
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
